import React, { useEffect, useState, useRef, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, Chip, IconButton, Dialog, DialogTitle, DialogContent, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Tooltip, CircularProgress, TextField } from "@mui/material";
import { ExpandMore, Psychology, ArrowBack } from "@mui/icons-material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import ModernToggle from "../ModernToggle";
import InfoIcon from '@mui/icons-material/Info';
import { People } from "@mui/icons-material";
import { Paper, DialogActions, Button, Card, CardContent, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Collapse } from "@mui/material";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { token_keys } from '../../constants';
import insightsApi from '../../api/insightsApi';
import TopicRecomputeDialog from './TopicRecomputeDialog';
import { formatResponseText } from '../../utils/responseUtils';
import { normalizeReportLanguage } from './signalLabels';

// Topic Detail Modal Component for New Structure
export const TopicDetailModal = ({ open, onClose, topic, questionTitle, statsData, reportId, question, topicIndex, initialTitle, onTitleSaved }) => {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedTab, setSelectedTab] = useState("filter");
    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [attributeAccordionExpanded, setAttributeAccordionExpanded] = useState(true);

    // Editable topic title (detail view). Any logged-in user may edit.
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState("");
    const [savingTitle, setSavingTitle] = useState(false);
    const [displayTitle, setDisplayTitle] = useState("");

    // Keep the shown title in sync when a (possibly overridden) title arrives.
    useEffect(() => {
        setDisplayTitle(initialTitle != null ? initialTitle : (topic ? topic.title : ""));
        setIsEditingTitle(false);
    }, [initialTitle, topic]);

    if (!topic) return null;

    const startEditTitle = () => {
        setTitleDraft(displayTitle);
        setIsEditingTitle(true);
    };

    const cancelEditTitle = () => {
        setIsEditingTitle(false);
    };

    const saveTitle = async () => {
        const next = (titleDraft || "").trim();
        if (!next || next === displayTitle) {
            setIsEditingTitle(false);
            return;
        }
        setSavingTitle(true);
        try {
            await insightsApi.editTopicTitle(reportId, {
                question,
                topic_id: topic && topic.topic_id != null ? topic.topic_id : null,
                topic_index: topicIndex,
                title: next,
            });
            setDisplayTitle(next);
            setIsEditingTitle(false);
            if (onTitleSaved) onTitleSaved(next);
            enqueueSnackbar(
                intl.formatMessage({ id: "topicsSection.title_updated", defaultMessage: "Topic title updated" }),
                { variant: "success" }
            );
        } catch (e) {
            // Revert to the previous title and surface a small error.
            setIsEditingTitle(false);
            enqueueSnackbar(
                intl.formatMessage({ id: "topicsSection.title_update_failed", defaultMessage: "Failed to update topic title" }),
                { variant: "error", autoHideDuration: 3000 }
            );
        } finally {
            setSavingTitle(false);
        }
    };

    const getSentimentColor = (diff) => {
        if (diff > 0) return "#4caf50";
        if (diff < 0) return "#f44336";
        return "#ff9800";
    };

    const handleAttributeClick = (attr) => {
        setSelectedAttribute(selectedAttribute === attr ? null : attr);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableScrollLock={true}>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        {isEditingTitle ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <TextField
                                    value={titleDraft}
                                    onChange={(e) => setTitleDraft(e.target.value)}
                                    size="small"
                                    autoFocus
                                    variant="outlined"
                                    disabled={savingTitle}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") { e.preventDefault(); saveTitle(); }
                                        if (e.key === "Escape") { cancelEditTitle(); }
                                    }}
                                    sx={{ minWidth: 300 }}
                                />
                                <Button
                                    onClick={saveTitle}
                                    size="small"
                                    variant="contained"
                                    disabled={savingTitle || !titleDraft.trim() || titleDraft.trim() === displayTitle}
                                >
                                    <FormattedMessage id="common.save" defaultMessage="Save" />
                                </Button>
                                <Button onClick={cancelEditTitle} size="small" disabled={savingTitle}>
                                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="h6">{displayTitle}</Typography>
                                <Tooltip title={intl.formatMessage({ id: "topicsSection.edit_title", defaultMessage: "Edit title" })}>
                                    <IconButton
                                        size="small"
                                        onClick={startEditTitle}
                                        sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        <Chip
                            label={intl.formatMessage({ id: "topicsSection.responses_count", defaultMessage: "{count} responses" }, { count: topic.count })}
                            size="small"
                            color="primary"
                            sx={{ mt: 1 }}
                        />
                    </Box>
                    <IconButton onClick={onClose}>
                        <ArrowBack />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Tabs value={selectedTab} onChange={(e, val) => setSelectedTab(val)} sx={{ mb: 2 }}>
                    <Tab value="responses" label={intl.formatMessage({ id: "topicsSection.tab_responses", defaultMessage: "Responses" })} />
                    <Tab value="filter" label={intl.formatMessage({ id: "topicsSection.tab_filter_analysis", defaultMessage: "Filter Analysis" })} />
                </Tabs>

                {selectedTab === "responses" && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}><FormattedMessage id="topicsSection.sample_responses" defaultMessage="Sample Responses" /></Typography>
                        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                            {topic.responses && topic.responses.length > 0 ? (
                                <Box>
                                    {topic.responses.map((response, index) => {
                                        const text = formatResponseText(response);
                                        return (
                                        <Accordion
                                            key={index}
                                            sx={{
                                                boxShadow: 'none',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '4px !important',
                                                mb: 1,
                                                bgcolor: index % 2 === 0 ? 'white' : '#fafafa',
                                                '&:before': { display: 'none' },
                                                '&.Mui-expanded': {
                                                    margin: 0,
                                                    mb: 1,
                                                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                                                    border: '1px solid rgba(25, 118, 210, 0.2)'
                                                }
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}
                                                sx={{
                                                    minHeight: 48,
                                                    '&.Mui-expanded': {
                                                        minHeight: 48,
                                                        bgcolor: 'rgba(25, 118, 210, 0.06)'
                                                    },
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: '8px 0',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        '&.Mui-expanded': {
                                                            margin: '8px 0'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Psychology color="primary" sx={{ fontSize: 20, flexShrink: 0 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {text.length > 100
                                                        ? text.substring(0, 100) + "..."
                                                        : text
                                                    }
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.primary',
                                                        lineHeight: 1.6,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    <FormattedMessage id="topicsSection.no_sample_responses" defaultMessage="No sample responses available" />
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {selectedTab === "filter" && (
                    <Box>

                        {/* Attribute Difference - Grouped by Question */}
                        <Accordion
                            expanded={attributeAccordionExpanded}
                            onChange={() => setAttributeAccordionExpanded(!attributeAccordionExpanded)}
                            sx={{ mb: 3 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                    <FormattedMessage id="topicsSection.percentage_diff_by_filter" defaultMessage="Percentage Difference by Filter" />
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {(() => {
                                    // Group attributes by questions from statsData
                                    const questions = statsData?.questions || [];
                                    const attrDiff = topic.attr_diff || {};

                                    // Create a map of attribute text to diff value
                                    const diffMap = new Map(Object.entries(attrDiff));

                                    // Track which attributes have been displayed
                                    const displayedAttrs = new Set();

                                    // Group attributes by their parent question
                                    const questionGroups = questions.map(question => {
                                        const questionChoices = (question.choices || [])
                                            .filter(choice => {
                                                const choiceText = choice.text;
                                                return choiceText && diffMap.has(choiceText) && !displayedAttrs.has(choiceText);
                                            })
                                            .map(choice => {
                                                displayedAttrs.add(choice.text);
                                                return {
                                                    text: choice.text,
                                                    diff: diffMap.get(choice.text)
                                                };
                                            });

                                        return {
                                            title: question.title,
                                            choices: questionChoices
                                        };
                                    }).filter(group => group.choices.length > 0);

                                    // Add any remaining attributes that weren't matched to a question
                                    const unmatchedAttrs = Array.from(diffMap.entries())
                                        .filter(([attr]) => !displayedAttrs.has(attr))
                                        .map(([text, diff]) => ({ text, diff }));

                                    if (unmatchedAttrs.length > 0) {
                                        questionGroups.push({
                                            title: intl.formatMessage({ id: "topicsSection.other", defaultMessage: "Other" }),
                                            choices: unmatchedAttrs
                                        });
                                    }

                                    let globalIndex = 0;

                                    return questionGroups.map((group, groupIndex) => (
                                        <Box key={groupIndex} sx={{ mb: groupIndex < questionGroups.length - 1 ? 2 : 0 }}>
                                            {/* Question header */}
                                            <Box sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                bgcolor: '#e8f4fc',
                                                borderRadius: 1,
                                                mb: 1,
                                                borderLeft: '3px solid #1976d2'
                                            }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        color: '#1565c0'
                                                    }}
                                                    title={group.title}
                                                >
                                                    {group.title}
                                                </Typography>
                                            </Box>
                                            {/* Attributes for this question */}
                                            {group.choices.map((choiceData, choiceIndex) => {
                                                const currentIndex = globalIndex++;
                                                const diff = choiceData.diff;

                                                return (
                                                    <Box
                                                        key={choiceIndex}
                                                        sx={{
                                                            mb: 1.5,
                                                            ml: 1,
                                                            cursor: "pointer",
                                                            border: selectedAttribute === choiceData.text ? "2px solid #1976d2" : "none",
                                                            borderRadius: 1,
                                                            p: selectedAttribute === choiceData.text ? 1 : 0,
                                                            bgcolor: selectedAttribute === choiceData.text ? 'rgba(218 227 239 / 0.84)' : currentIndex % 2 === 0 ? 'white' : '#fafafa'
                                                        }}
                                                        onClick={() => handleAttributeClick(choiceData.text)}
                                                    >
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                            <Typography variant="body2">{choiceData.text}</Typography>
                                                            <Chip
                                                                label={`${(diff * 100).toFixed(2)}%`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: getSentimentColor(diff),
                                                                    color: "white"
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                width: "100%",
                                                                height: 8,
                                                                bgcolor: "#e0e0e0",
                                                                borderRadius: 1,
                                                                overflow: "hidden",
                                                                position: "relative"
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    left: "50%",
                                                                    width: `${Math.abs(diff) * 50}%`,
                                                                    height: "100%",
                                                                    bgcolor: getSentimentColor(diff),
                                                                    transform: diff < 0 ? "translateX(-100%)" : "none",
                                                                    transition: "width 0.3s ease"
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    left: "50%",
                                                                    width: 2,
                                                                    height: "100%",
                                                                    bgcolor: "#000",
                                                                    transform: "translateX(-50%)"
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    ));
                                })()}
                            </AccordionDetails>
                        </Accordion>

                        {/* Attribute Topic Count - Grouped by Question */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
                                <FormattedMessage id="topicsSection.response_count_by_filter" defaultMessage="Response Count by Filter" />
                            </Typography>
                            {(() => {
                                // Group attributes by questions from statsData
                                const questions = statsData?.questions || [];
                                const attrCount = topic.attr_topic_count || {};

                                // Create a map of attribute text to count value
                                const countMap = new Map(Object.entries(attrCount));

                                // Track which attributes have been displayed
                                const displayedAttrs = new Set();

                                // Group attributes by their parent question
                                const questionGroups = questions.map(question => {
                                    const questionChoices = (question.choices || [])
                                        .filter(choice => {
                                            const choiceText = choice.text;
                                            return choiceText && countMap.has(choiceText) && !displayedAttrs.has(choiceText);
                                        })
                                        .map(choice => {
                                            displayedAttrs.add(choice.text);
                                            return {
                                                text: choice.text,
                                                count: countMap.get(choice.text)
                                            };
                                        });

                                    return {
                                        title: question.title,
                                        choices: questionChoices
                                    };
                                }).filter(group => group.choices.length > 0);

                                // Add any remaining attributes that weren't matched to a question
                                const unmatchedAttrs = Array.from(countMap.entries())
                                    .filter(([attr]) => !displayedAttrs.has(attr))
                                    .map(([text, count]) => ({ text, count }));

                                if (unmatchedAttrs.length > 0) {
                                    questionGroups.push({
                                        title: intl.formatMessage({ id: "topicsSection.other", defaultMessage: "Other" }),
                                        choices: unmatchedAttrs
                                    });
                                }

                                let globalIndex = 0;

                                return questionGroups.map((group, groupIndex) => (
                                    <Box key={groupIndex} sx={{ mb: 3 }}>
                                        {/* Question header */}
                                        <Box sx={{
                                            px: 1.5,
                                            py: 0.75,
                                            bgcolor: '#e8f4fc',
                                            borderRadius: 1,
                                            mb: 1.5,
                                            borderLeft: '3px solid #1976d2'
                                        }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#1565c0'
                                                }}
                                                title={group.title}
                                            >
                                                {group.title}
                                            </Typography>
                                        </Box>
                                        {/* Count cards for this question */}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, ml: 1 }}>
                                            {group.choices.map((choiceData, choiceIndex) => {
                                                const currentIndex = globalIndex++;

                                                return (
                                                    <Paper
                                                        key={choiceIndex}
                                                        sx={{
                                                            p: 1.5,
                                                            minWidth: 100,
                                                            textAlign: "center",
                                                            cursor: "pointer",
                                                            border: selectedAttribute === choiceData.text ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                                            bgcolor: selectedAttribute === choiceData.text ? 'rgba(218 227 239 / 0.84)' : currentIndex % 2 === 0 ? 'white' : '#fafafa',
                                                            transition: "border-color 0.2s ease"
                                                        }}
                                                        onClick={() => handleAttributeClick(choiceData.text)}
                                                    >
                                                        <Typography variant="h6" color="primary.main">
                                                            {choiceData.count}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                            {choiceData.text}
                                                        </Typography>
                                                    </Paper>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ));
                            })()}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}><FormattedMessage id="topicsSection.close" defaultMessage="Close" /></Button>
            </DialogActions>
        </Dialog>
    );
};

// Base URL for backend
const BACKEND_BASE_URL =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : null);

const getWebSocketUrl = (httpUrl) => {
    if (httpUrl.startsWith('https://')) return httpUrl.replace('https://', 'wss://');
    if (httpUrl.startsWith('http://')) return httpUrl.replace('http://', 'ws://');
    return httpUrl;
};

export const CellInsightDialog = ({ open, onClose, reportId, query }) => {
    const intl = useIntl();
    const [response, setResponse] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);

    const startStream = useCallback(() => {
        if (!reportId || !query) return;

        setResponse('');
        setError(null);
        setLoading(true);

        const accessToken = localStorage.getItem(token_keys.access_token);
        const wsBaseUrl = getWebSocketUrl(BACKEND_BASE_URL);
        const url = `${wsBaseUrl}/ws/rag/`;

        const ws = new WebSocket(url, ["Bearer", accessToken]);
        wsRef.current = ws;

        ws.onopen = () => {
            ws.send(JSON.stringify({
                user_input: query,
                history: [],
                report_ids: [reportId],
                channel: 'topic_table_cell_info',
                session_id: crypto.randomUUID(),
                document_ids: [],
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.done) {
                    ws.close();
                    setLoading(false);
                    return;
                }
                if (!data.message) return;
                const chunk = data.message
                    .split('\n\n')
                    .map(p => p.replace('data: ', ''))
                    .join('')
                    .replaceAll('$n$l$', '\n');
                setResponse(prev => prev + chunk);
            } catch (err) {
                console.error('CellInsight parse error:', err);
            }
        };

        ws.onerror = () => {
            setError(intl.formatMessage({ id: 'topicsSection.cell_insight_error', defaultMessage: 'Connection error. Please try again.' }));
            setLoading(false);
        };

        ws.onclose = (event) => {
            if (!event.wasClean && loading) {
                setError(intl.formatMessage({ id: 'topicsSection.cell_insight_lost', defaultMessage: 'Connection lost.' }));
                setLoading(false);
            }
        };
    }, [reportId, query, intl]);

    useEffect(() => {
        if (open && query) {
            startStream();
        }
        return () => {
            wsRef.current?.close();
        };
    }, [open, query]);

    const handleClose = () => {
        wsRef.current?.close();
        setResponse('');
        setError(null);
        setLoading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableScrollLock>
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
            }}>
                <AutoAwesomeIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <FormattedMessage id="topicsSection.cell_insight" defaultMessage="Cell Insight" />
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, minHeight: 150 }}>
                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                )}
                {loading && !response && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {response && (
                    <Box sx={{
                        '& p': { mt: 0, mb: 1 },
                        '& ul, & ol': { pl: 2, mb: 1 },
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        // GFM tables need both the plugin below to parse and these
                        // borders to be readable; wide ones scroll inside the dialog
                        overflowX: 'auto',
                        '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13, my: 1 },
                        '& th, & td': { border: '0.5px solid rgba(21,101,192,0.15)', padding: '6px 10px', textAlign: 'left' },
                        '& th': { background: 'rgba(21,101,192,0.06)', color: '#0d47a1', fontWeight: 500 },
                    }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{response}</ReactMarkdown>
                        {loading && <span style={{ opacity: 0.5 }}>▊</span>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={handleClose} variant="outlined" size="small">
                    <FormattedMessage id="topicsSection.close" defaultMessage="Close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Topics Section Component - Heatmap Table Design
//
// `variant`:
//   "topics"  (default) — the report's topics view, unchanged.
//   "signals" — same heatmap, but driven by signal data: the pillar bar chart is
//               hidden (the Risk tab owns it), the pillar is chosen externally via
//               `externalSelectedPillar`, rows are sorted by mention count (desc),
//               and each row title is tinted/marked by its `is_negative` flag.
const TopicsSection = ({ topicsData, pillarSentimentMeans, statsData, reportId, reportName, variant = "topics", externalSelectedPillar = null, language = null }) => {
    const isSignals = variant === "signals";
    // Which language the Ask-Prompta prompts below are written in. `language` is
    // the report's own `SurveyInsight.language`, served on the report-detail
    // response — the same column the BE writes the report prose from.
    //
    // The fallback is the old rule: the report's TITLE containing "spanish".
    // That was the only signal available before the column was exposed, and it
    // is wrong twice over — a Spanish report named "Wave 3" reads as English,
    // and it never fired at all in the Risk tab, which is not passed a
    // reportName. It stays only so a response without `language` (an older BE)
    // behaves exactly as it does today.
    const isSpanish = language != null
        ? normalizeReportLanguage(language) === "es"
        : (reportName || '').toLowerCase().includes('spanish');
    const intl = useIntl();
    const [selectedPillar, setSelectedPillar] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [pillarViewMode, setPillarViewMode] = useState("bar"); // "bar" or "table"
    const [expandedQuestions, setExpandedQuestions] = useState({}); // Track which questions are expanded
    const [cellInsightOpen, setCellInsightOpen] = useState(false);
    const [cellInsightQuery, setCellInsightQuery] = useState('');
    // Which topic (question + index) the detail modal is showing, and any
    // locally-edited titles so the table reflects an edit immediately.
    const [selectedTopicMeta, setSelectedTopicMeta] = useState({ question: null, topicIndex: null });
    const [titleOverrides, setTitleOverrides] = useState({});
    // Which question's recompute dialog is open, and the topic lists that have
    // been recomputed in this session. The confirmed topics come back from the
    // API, so the table can show them without refetching the whole report.
    const [recomputeQuestion, setRecomputeQuestion] = useState(null);
    const [topicOverrides, setTopicOverrides] = useState({});

    // In signals variant the pillar is controlled by the parent (Risk tab); in
    // topics variant it's the locally-selected pillar from the bar chart.
    const effectivePillar = isSignals ? externalSelectedPillar : selectedPillar;

    const resolveTitle = (questionKey, topicIndex, original) => {
        const key = `${questionKey}::${topicIndex}`;
        return titleOverrides[key] != null ? titleOverrides[key] : original;
    };

    const handleTitleSaved = (newTitle) => {
        const { question, topicIndex } = selectedTopicMeta;
        if (question == null || topicIndex == null) return;
        setTitleOverrides(prev => ({ ...prev, [`${question}::${topicIndex}`]: newTitle }));
    };

    const handleTopicsRecomputed = (question, topics) => {
        setTopicOverrides(prev => ({ ...prev, [question]: topics }));
        // Per-topic title edits were keyed to the OLD rows of this question, so
        // they would now rename whatever landed at that index. Drop them.
        setTitleOverrides(prev => {
            const next = {};
            Object.keys(prev).forEach(key => {
                if (!key.startsWith(`${question}::`)) next[key] = prev[key];
            });
            return next;
        });
        setRecomputeQuestion(null);
    };

    const handleCellInsight = ({ questionTitle, pillar, topicName, groupName, colorLabel, cellValue, topicResponses, groupCountInTopic, topicPercentage, totalGroupCount, avgResponsesPerTopic, overallPercentage }) => {
        const query = isSpanish
            ? `Explica esta celda del mapa de calor en un lenguaje claro para una audiencia ejecutiva.

Contexto:
- Pregunta: "${questionTitle}"
- Pilar: "${pillar}"
- Tema: "${topicName}"
- Grupo: "${groupName}"
- Etiqueta de la celda: ${colorLabel}
- Valor de la celda: ${cellValue}

Cómo se calcula esta celda:
- Dentro de este tema, ${groupCountInTopic} de ${topicResponses} respuestas pertenecen a "${groupName}" (${topicPercentage}%).
- En la población total del filtro, ${avgResponsesPerTopic} de ${totalGroupCount} personas pertenecen a "${groupName}" (${overallPercentage}%).
- El valor de la celda (${cellValue}) refleja la diferencia entre el porcentaje específico del tema y el porcentaje general.

Qué explicar:
- Por qué este valor es positivo, negativo o neutro
- Qué significa en cuanto a la sobrerrepresentación o subrepresentación de este grupo en este tema
- Si la diferencia parece significativa o menor
- Qué debería inferir un líder a partir de este resultado

Escribe la respuesta a nivel ejecutivo:
- sé directo
- sé útil
- evita el lenguaje técnico
- enfócate en el significado para el negocio, no en los detalles matemáticos; a menos que sean necesarios para la claridad`
            : `Explain this heatmap cell in plain language for an executive audience.

Context:
- Question: "${questionTitle}"
- Pillar: "${pillar}"
- Topic: "${topicName}"
- Group: "${groupName}"
- Cell label: ${colorLabel}
- Cell value: ${cellValue}

How this cell is calculated:
- Within this topic, ${groupCountInTopic} out of ${topicResponses} responses belong to "${groupName}" (${topicPercentage}%).
- In the overall filter population, ${avgResponsesPerTopic} out of ${totalGroupCount} people belong to "${groupName}" (${overallPercentage}%).
- The cell value (${cellValue}) reflects the difference between the topic-specific percentage and the overall percentage.

What to explain:
- Why this value is positive, negative, or neutral
- What it means about how overrepresented or underrepresented this group is in this topic
- Whether the difference appears meaningful or minor
- What a leader should infer from this result

Write the response at an executive level:
- be direct
- be helpful
- avoid technical jargon
- focus on the business meaning, not the math details; unless needed for clarity`;
        setCellInsightQuery(query);
        setCellInsightOpen(true);
    };

    // Toggle question expansion
    const toggleQuestionExpanded = (questionText) => {
        setExpandedQuestions(prev => ({
            ...prev,
            // If undefined (first time), treat as expanded and set to false (collapse)
            // Otherwise toggle the current value
            [questionText]: prev[questionText] === undefined ? false : !prev[questionText]
        }));
    };

    // Check if question is expanded (default to expanded)
    const isQuestionExpanded = (questionText) => {
        return expandedQuestions[questionText] !== false; // Default to expanded (true)
    };

    // Initialize with first pillar. The pillar sentiment means are the preferred
    // source (they drive the bar chart above), but a report can carry topics
    // without them — fall back to the pillars the topics themselves declare so the
    // cards below always have a pillar to render against.
    useEffect(() => {
        if (selectedPillar) return;
        if (pillarSentimentMeans && Object.keys(pillarSentimentMeans).length > 0) {
            setSelectedPillar(Object.keys(pillarSentimentMeans)[0]);
            return;
        }
        const firstTopicPillar = Object.values(topicsData || {})
            .map(topics => (topics && topics.length > 0 ? topics[0].pillar : null))
            .find(pillar => pillar != null);
        if (firstTopicPillar) {
            setSelectedPillar(firstTopicPillar);
        }
    }, [pillarSentimentMeans, topicsData, selectedPillar]);

    // Auto-select the first demographic question, when the report has any. This
    // selection scopes ONLY the Filter-Analysis columns inside each topic card; a
    // report with no demographic questions leaves it null and still renders its
    // topic cards, just without the demographic breakdown.
    useEffect(() => {
        if (statsData && statsData.questions && statsData.questions.length > 0 && !selectedQuestion) {
            const firstQuestion = statsData.questions[0];
            if (firstQuestion && firstQuestion.choices && firstQuestion.choices.length > 0) {
                setSelectedQuestion(firstQuestion);
            }
        }
    }, [statsData, selectedQuestion]);

    if (!topicsData || Object.keys(topicsData).length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}><FormattedMessage id="topicsSection.topics_analysis" defaultMessage="Topics Analysis" /></Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="topicsSection.no_topics_data" defaultMessage="No topics data available" />
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const getPillarSentimentColor = (sentiment) => {
        // Convert sentiment to percentage (assuming sentiment is between -1 and 1)
        const percentage = sentiment * 100;

        if (percentage <= -10) return "#f44336"; // Red
        if (percentage <= -1) return "#ff9800"; // Orange
        if (percentage <= 1) return "#ffeb3b"; // Yellow
        if (percentage <= 10) return "#9acd32"; // Yellowish green (more greeny)
        if (percentage <= 20) return "#4caf50"; // Normal green
        return "#2e7d32"; // Darker green
    };

    const getPillarTextColor = (sentiment) => {
        // Convert sentiment to percentage (assuming sentiment is between -1 and 1)
        const percentage = sentiment * 100;

        // Use black text for yellow background, white for others
        if (percentage <= 10 && percentage >= -1) return "#000000"; // Black for yellow
        return "#ffffff"; // White for all other colors
    };

    const getCellBackgroundColor = (diff) => {
        if (diff === 0) return "#fff";

        // Take absolute value of diff and normalize to 0-1 range (assuming max diff is 1.0)
        const absDiff = Math.abs(diff);
        console.log(absDiff);
        const intensity = Math.min(absDiff, 100); // Cap at 1.0 (100% intensity)

        if (diff > 0) {
            // Positive difference - green with intensity-based opacity
            return `rgba(76, 175, 80, ${intensity / 100})`; // Green with opacity 0-0.8
        } else {
            // Negative difference - red with intensity-based opacity
            return `rgba(244, 67, 54, ${intensity / 100})`; // Red with opacity 0-0.8
        }
    };

    const handleViewTopicDetails = (topic, questionText, topicIndex) => {
        setSelectedTopic(topic);
        setSelectedTopicMeta({ question: questionText, topicIndex });
        setModalOpen(true);
    };

    // Get all MCQ choices and their total counts.
    //
    // `scopedTopics` (optional): when passed the topics rendered in ONE table,
    // `clustered` is summed over just those topics so it equals the sum of that
    // column's visible cells. When omitted, `clustered` is 0 (used for the cell
    // grid / tooltip, which don't consume it). selfCount/totalCount are always
    // the all-questions figures the in-cell tooltip math relies on — unchanged.
    const getMCQChoicesWithCounts = (scopedTopics = null) => {
        if (!selectedQuestion || !selectedQuestion.choices) {
            return [];
        }

        const choiceTotals = {};

        // Collect counts for selected question's choices across all topics
        Object.values(topicsData).forEach(topics => {
            topics.forEach(topic => {
                selectedQuestion.choices.forEach(choice => {
                    const choiceText = choice.text;
                    const count = topic.attr_topic_count?.[choiceText] || 0;
                    if (!choiceTotals[choiceText]) {
                        choiceTotals[choiceText] = 0;
                    }
                    choiceTotals[choiceText] += count;
                });
            });
        });

        // Clustered per-group counts scoped to just the topics shown in this
        // table (X in the "X / Y Responded" header). Σ of the column's visible
        // cells equals this because the cells read the same attr_topic_count.
        const clusteredTotals = {};
        (scopedTopics || []).forEach(topic => {
            selectedQuestion.choices.forEach(choice => {
                const choiceText = choice.text;
                clusteredTotals[choiceText] =
                    (clusteredTotals[choiceText] || 0) + (topic.attr_topic_count?.[choiceText] || 0);
            });
        });

        return selectedQuestion.choices.map(choice => ({
            name: choice.text,
            // Canonical per-group response total (Y) — the same value the
            // segmentation section (FiltersSection) shows (choice.responses_count).
            groupTotal: choice.responses_count || 0,
            // Clustered count (X) — this table's topics only; 0 when unscoped.
            clustered: clusteredTotals[choice.text] || 0,
            // selfCount/totalCount: all-questions figures consumed only by the
            // in-cell tooltip math (left unchanged).
            totalCount: choiceTotals[choice.text] || 0,
            selfCount: Math.round(choiceTotals[choice.text] / Object.keys(topicsData).length)
        }));
    };

    const mcqChoicesWithCounts = getMCQChoicesWithCounts();

    // The question -> topics entries belonging to the selected pillar. Derived up
    // front so the cards block can both render them and tell when there are none,
    // instead of falling through to a silent blank.
    const pillarTopicEntries = Object.entries(topicsData).filter(([questionText, originalTopics]) => {
        const rawTopics = topicOverrides[questionText] || originalTopics;
        const pillar = rawTopics && rawTopics.length > 0 ? rawTopics[0].pillar : null;
        return pillar != null && pillar === effectivePillar;
    });

    // Whether any topic declares a pillar at all. When none does, no selection can
    // ever match, so the empty state below stands in rather than waiting on a
    // pillar that will never arrive.
    const hasAnyTopicPillar = Object.values(topicsData).some(
        topics => topics && topics.length > 0 && topics[0].pillar != null
    );

    // Get keyword font size for word cloud
    const getKeywordFontSize = (keywords) => {
        if (!keywords || Object.keys(keywords).length === 0) return {};

        const frequencies = Object.values(keywords);
        const maxFreq = Math.max(...frequencies);
        const minFreq = Math.min(...frequencies);

        return Object.entries(keywords).reduce((acc, [word, freq]) => {
            const minSize = 12;
            const maxSize = 28;
            const normalized = (freq - minFreq) / (maxFreq - minFreq || 1);
            acc[word] = minSize + normalized * (maxSize - minSize);
            return acc;
        }, {});
    };

    return (
        <Box>
            {/* Pillar Sentiment Chart - Clickable with Gauge (topics variant only) */}
            {!isSignals && pillarSentimentMeans && Object.keys(pillarSentimentMeans).length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Box>
                                <Typography variant="h6"><FormattedMessage id="topicsSection.pillar_sentiment_scores" defaultMessage="Change Signals by Pillar" /></Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <FormattedMessage id="topicsSection.pillar_sentiment_hint" defaultMessage="Average tone of what people wrote, per pillar." />
                                </Typography>
                            </Box>
                            <ModernToggle
                                leftLabel={intl.formatMessage({ id: "topicsSection.bar_chart", defaultMessage: "Bar Chart" })}
                                rightLabel={intl.formatMessage({ id: "topicsSection.table", defaultMessage: "Table" })}
                                checked={pillarViewMode === "table"}
                                onChange={(checked) => setPillarViewMode(checked ? "table" : "bar")}
                            />
                        </Box>

                        {pillarViewMode === "bar" ? (
                            /* Bar Chart View */
                            <Grid container spacing={3}>
                                {/* Pillar List - Left Side */}
                                <Grid item xs={12} md={7}>
                                    {Object.entries(pillarSentimentMeans).map(([pillar, sentiment], index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 1.5,
                                                cursor: "pointer",
                                                opacity: selectedPillar === pillar ? 1 : 0.7,
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    opacity: 1,
                                                    transform: "translateX(4px)"
                                                }
                                            }}
                                            onClick={() => setSelectedPillar(pillar)}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
                                                        fontWeight: selectedPillar === pillar ? "bold" : "medium",
                                                        color: selectedPillar === pillar ? "primary.main" : "text.primary"
                                                    }}
                                                >
                                                    {pillar}
                                                </Typography>
                                                <Chip
                                                    label={`${(sentiment > 0 ? "+" : "")}${(sentiment * 100).toFixed(2)}%`}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
                                                        bgcolor: getPillarSentimentColor(sentiment),
                                                        color: getPillarTextColor(sentiment)
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    height: 20,
                                                    bgcolor: "#f0f0f0",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    position: "relative",
                                                    border: selectedPillar === pillar ? "2px solid" : "none",
                                                    borderColor: "primary.main"
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        width: `${Math.abs(sentiment) * 50}%`,
                                                        height: "100%",
                                                        bgcolor: getPillarSentimentColor(sentiment),
                                                        transform: sentiment < 0 ? "translateX(-100%)" : "none",
                                                        transition: "width 0.3s ease"
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        width: 2,
                                                        height: "100%",
                                                        bgcolor: "#000",
                                                        transform: "translateX(-50%)"
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Grid>

                                {/* Gauge Meter - Right Side */}
                                <Grid item xs={12} md={5}>
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                        {/* Gauge SVG */}
                                        <Box sx={{ position: "relative", width: 200, height: 120 }}>
                                            <svg viewBox="0 0 200 120" width="200" height="120">
                                                {/* Gauge background arc segments - three sections with gaps */}
                                                {/* Red section - left (negative): 180° to 130° */}
                                                <path
                                                    d="M 20 100 A 80 80 0 0 1 52.6 30.7"
                                                    fill="none"
                                                    stroke="#f44336"
                                                    strokeWidth="16"
                                                    strokeLinecap="round"
                                                />

                                                {/* Yellow section - middle (neutral): 110° to 70° */}
                                                <path
                                                    d="M 72.6 24.9 A 80 80 0 0 1 127.4 24.9"
                                                    fill="none"
                                                    stroke="#ffeb3b"
                                                    strokeWidth="16"
                                                    strokeLinecap="round"
                                                />

                                                {/* Green section - right (positive): 50° to 0° */}
                                                <path
                                                    d="M 146.4 36.7 A 80 80 0 0 1 180 100"
                                                    fill="none"
                                                    stroke="#4caf50"
                                                    strokeWidth="16"
                                                    strokeLinecap="round"
                                                />

                                                {/* Inner gray arc for depth */}

                                                {/* Tick marks */}
                                                {[-90, 0, 90].map((angle, i) => {
                                                    const rad = (angle - 90) * Math.PI / 180;
                                                    const x1 = 100 + 85 * Math.cos(rad);
                                                    const y1 = 100 + 85 * Math.sin(rad);
                                                    const x2 = 100 + 75 * Math.cos(rad);
                                                    const y2 = 100 + 75 * Math.sin(rad);
                                                    return (
                                                        <line
                                                            key={i}
                                                            x1={x1}
                                                            y1={y1}
                                                            x2={x2}
                                                            y2={y2}
                                                            stroke="#666"
                                                            strokeWidth={i === 4 ? 2 : 1}
                                                        />
                                                    );
                                                })}

                                                {/* Needle */}
                                                <g
                                                    style={{
                                                        transform: `rotate(${(pillarSentimentMeans[selectedPillar] || 0) * 90}deg)`,
                                                        transformOrigin: '100px 100px',
                                                        transition: 'transform 0.6s cubic-bezier(0.4, 2.08, 0.55, 0.44)'
                                                    }}
                                                >
                                                    <polygon
                                                        points="100,25 96,100 104,100"
                                                        fill="#333"
                                                    />
                                                    <circle cx="100" cy="100" r="8" fill="#333" />
                                                    <circle cx="100" cy="100" r="4" fill="#fff" />
                                                </g>

                                                {/* Labels */}
                                                <text x="15" y="115" fontSize="10" fill="#f44336" fontWeight="bold">-</text>
                                                <text x="180" y="115" fontSize="10" fill="#4caf50" fontWeight="bold">+</text>
                                            </svg>
                                        </Box>

                                        {/* Selected Pillar Info */}
                                        {selectedPillar && (
                                            <Box sx={{ textAlign: "center", mt: 1 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "primary.main",
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                                    }}
                                                >
                                                    {selectedPillar}
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: getPillarSentimentColor(pillarSentimentMeans[selectedPillar] || 0),
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
                                                        textShadow: '0.5px 0.5px 0px #00000070, -0.5px -0.5px 0px #00000070, 0.5px -0.5px 0px #00000070, -0.5px 0.5px 0px #00000070'
                                                    }}
                                                >
                                                    {((pillarSentimentMeans[selectedPillar] || 0) > 0 ? "+" : "")}
                                                    {((pillarSentimentMeans[selectedPillar] || 0) * 100).toFixed(2)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        ) : (
                            /* Table View */
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        maxWidth: 550,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        borderRadius: 2
                                    }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#1a365d' }}>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}>
                                                    <FormattedMessage id="topicsSection.pillar" defaultMessage="Pillar" />
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}>
                                                    <FormattedMessage id="topicsSection.sentiment_score" defaultMessage="Sentiment Score" />
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}>
                                                    <FormattedMessage id="topicsSection.sentiment" defaultMessage="Sentiment" />
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(pillarSentimentMeans).map(([pillar, sentiment], index) => (
                                                <TableRow
                                                    key={index}
                                                    onClick={() => setSelectedPillar(pillar)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        bgcolor: selectedPillar === pillar ? 'rgba(25, 118, 210, 0.08)' : (index % 2 === 0 ? '#f9f9f9' : 'white'),
                                                        '&:hover': {
                                                            bgcolor: 'rgba(25, 118, 210, 0.12)'
                                                        },
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                >
                                                    <TableCell sx={{
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
                                                        fontWeight: selectedPillar === pillar ? 'bold' : 'normal',
                                                        color: selectedPillar === pillar ? 'primary.main' : 'text.primary',
                                                        borderLeft: selectedPillar === pillar ? '4px solid #1976d2' : '4px solid transparent',
                                                        transition: 'border-color 0.2s ease'
                                                    }}>
                                                        {pillar}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{
                                                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
                                                        fontWeight: 'bold',
                                                        color: getPillarSentimentColor(sentiment),
                                                        textShadow: (sentiment * 100 > -1 && sentiment * 100 <= 1) ? '1px 1px 0px #00000050, -1px -1px 0px #00000050, 1px -1px 0px #00000050, -1px 1px 0px #00000050' : 'none'
                                                    }}>
                                                        {(sentiment > 0 ? "+" : "")}{(sentiment * 100).toFixed(2)}%
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {/* Mini Gauge */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <svg viewBox="0 0 200 120" width="70" height="42">
                                                                {/* Red section */}
                                                                <path
                                                                    d="M 20 100 A 80 80 0 0 1 52.6 30.7"
                                                                    fill="none"
                                                                    stroke="#f44336"
                                                                    strokeWidth="16"
                                                                    strokeLinecap="round"
                                                                />
                                                                {/* Yellow section */}
                                                                <path
                                                                    d="M 72.6 24.9 A 80 80 0 0 1 127.4 24.9"
                                                                    fill="none"
                                                                    stroke="#ffeb3b"
                                                                    strokeWidth="16"
                                                                    strokeLinecap="round"
                                                                />
                                                                {/* Green section */}
                                                                <path
                                                                    d="M 146.4 36.7 A 80 80 0 0 1 180 100"
                                                                    fill="none"
                                                                    stroke="#4caf50"
                                                                    strokeWidth="16"
                                                                    strokeLinecap="round"
                                                                />
                                                                {/* Tick marks */}
                                                                {[-90, 0, 90].map((angle, i) => {
                                                                    const rad = (angle - 90) * Math.PI / 180;
                                                                    const x1 = 100 + 85 * Math.cos(rad);
                                                                    const y1 = 100 + 85 * Math.sin(rad);
                                                                    const x2 = 100 + 75 * Math.cos(rad);
                                                                    const y2 = 100 + 75 * Math.sin(rad);
                                                                    return (
                                                                        <line
                                                                            key={i}
                                                                            x1={x1}
                                                                            y1={y1}
                                                                            x2={x2}
                                                                            y2={y2}
                                                                            stroke="#666"
                                                                            strokeWidth={1}
                                                                        />
                                                                    );
                                                                })}
                                                                {/* Needle */}
                                                                <g
                                                                    style={{
                                                                        transform: `rotate(${sentiment * 90}deg)`,
                                                                        transformOrigin: '100px 100px',
                                                                        transition: 'transform 0.6s cubic-bezier(0.4, 2.08, 0.55, 0.44)'
                                                                    }}
                                                                >
                                                                    <polygon
                                                                        points="100,25 96,100 104,100"
                                                                        fill="#333"
                                                                    />
                                                                    <circle cx="100" cy="100" r="8" fill="#333" />
                                                                    <circle cx="100" cy="100" r="4" fill="#fff" />
                                                                </g>
                                                                {/* Labels */}
                                                                <text x="15" y="115" fontSize="10" fill="#f44336" fontWeight="bold">-</text>
                                                                <text x="180" y="115" fontSize="10" fill="#4caf50" fontWeight="bold">+</text>
                                                            </svg>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Question Selector - Horizontal Buttons */}
            {statsData && statsData.questions && statsData.questions.length > 0 && (
                <Card
                    sx={{
                        mb: 3,
                        background: 'linear-gradient(135deg, rgba(0 119 255 / 0.19) 0%, rgba(0 179 255 / 0.19) 100%)',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                >
                    <CardContent>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            justifyContent: 'center'
                        }}>
                            {statsData.questions.map((question, index) => (
                                <Button
                                    key={index}
                                    variant={selectedQuestion === question ? "contained" : "outlined"}
                                    onClick={() => setSelectedQuestion(question)}
                                    sx={{
                                        backgroundColor: selectedQuestion === question ? '#1a365d' : 'transparent',
                                        borderColor: '#1a365d',
                                        color: selectedQuestion === question ? '#ffffff' : '#1a365d',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: 2,
                                        minWidth: 'auto',
                                        '&:hover': {
                                            backgroundColor: selectedQuestion === question ? '#0f2440' : 'rgba(26, 54, 93, 0.08)',
                                            borderColor: '#1a365d',
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {question.title.length > 60 ? question.title.substring(0, 60) + "..." : question.title}
                                </Button>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Open-Text Question Cards with Heatmap Tables.
                Gated on the pillar alone: `selectedQuestion` is the demographic
                filter, and it scopes only the Filter-Analysis columns inside each
                card (below). A report with no demographic questions must still
                show its topics. */}
            {pillarTopicEntries.length > 0 && (
                <Box>
                    {Object.entries(topicsData).map(([questionText, originalTopics], questionIndex) => {
                        // A question recomputed in this session shows its new
                        // topics immediately, without refetching the report.
                        const rawTopics = topicOverrides[questionText] || originalTopics;
                        const fontSizes = {};
                        const pillar = rawTopics && rawTopics.length > 0 ? rawTopics[0].pillar : null;
                        if (pillar === null || pillar !== effectivePillar) {
                            return null;
                        }
                        // Signals: order rows by mention count (desc) so the most-
                        // mentioned signals sit on top and near-empty ones sink.
                        const topics = isSignals
                            ? [...rawTopics].sort((a, b) => (b.count || 0) - (a.count || 0))
                            : rawTopics;
                        topics.forEach(topic => {
                            fontSizes[topic.title] = getKeywordFontSize(topic.keywords);
                        });

                        return (
                            <Card key={questionIndex} sx={{ mb: 3 }}>
                                <CardContent>
                                    {/* Question Title - Clickable Accordion Header */}
                                    <Box
                                        onClick={() => toggleQuestionExpanded(questionText)}
                                        sx={{
                                            bgcolor: "primary.main",
                                            color: "white",
                                            p: 2,
                                            mb: isQuestionExpanded(questionText) ? 3 : 0,
                                            borderRadius: isQuestionExpanded(questionText) ? 1 : '4px 4px 0 0',
                                            mx: -2,
                                            mt: -2,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            transition: "all 0.3s ease",
                                            '&:hover': {
                                                bgcolor: "primary.dark"
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    color: "white",
                                                    p: 0.5,
                                                    transition: "transform 0.3s ease",
                                                    transform: isQuestionExpanded(questionText) ? "rotate(0deg)" : "rotate(-90deg)"
                                                }}
                                            >
                                                <ExpandMore />
                                            </IconButton>
                                            <Typography variant="h6" sx={{ flex: 1 }}>
                                                {questionText}
                                            </Typography>
                                        </Box>
                                        {/* Recompute this question's topics. Topics only:
                                            the signals variant renders the same table from
                                            signal data, which is not a topic model. */}
                                        {!isSignals && (
                                            <Tooltip title={intl.formatMessage({ id: "topicRecompute.tooltip", defaultMessage: "Recompute topics" })}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        // The header itself collapses the table.
                                                        e.stopPropagation();
                                                        setRecomputeQuestion(questionText);
                                                    }}
                                                    sx={{ color: "white", mr: 1 }}
                                                >
                                                    {/* Sliders, not the AI sparkle: that one already
                                                        marks the Ask-Prompta actions in this table, and
                                                        this button opens an editor rather than firing
                                                        off a generation. */}
                                                    <TuneIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Box sx={{
                                            bgcolor: "rgba(255, 255, 255, 0.2)",
                                            backdropFilter: "blur(10px)",
                                            border: "1px solid rgba(255, 255, 255, 0.3)",
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.5,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5
                                        }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.5,
                                                    fontSize: "0.75rem",
                                                    fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                                }}
                                            >
                                                {pillar}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Heatmap Table - Collapsible */}
                                    <Collapse in={isQuestionExpanded(questionText)} timeout="auto">
                                        <Box sx={{ overflow: "auto", pt: isQuestionExpanded(questionText) ? 0 : 2 }}>
                                            {(() => {
                                                const numChoiceCols = mcqChoicesWithCounts.length;
                                                const choiceColMin = 100;
                                                const gridMinWidth = 200 + numChoiceCols * choiceColMin;
                                                // No demographic question means no choice columns, and
                                                // `repeat(0, ...)` is invalid CSS — the topic column then
                                                // spans the table on its own.
                                                const gridTemplateColumns = numChoiceCols > 0
                                                    ? `200px repeat(${numChoiceCols}, minmax(${choiceColMin}px, 1fr))`
                                                    : `minmax(200px, 1fr)`;
                                                // Header counts scoped to THIS table's topics so the "X / Y
                                                // Responded" clustered total matches the sum of the visible cells.
                                                const mcqChoicesForHeader = getMCQChoicesWithCounts(topics);
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns,
                                                            minWidth: gridMinWidth,
                                                            width: numChoiceCols > 0 ? "max-content" : "100%"
                                                        }}
                                                    >
                                                        {/* Table Header - first row in grid */}
                                                        <Box sx={{ p: 2, fontWeight: "bold", background: "#f0f4f8", color: "#1e3a5f", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "2px solid #cbd5e1" }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}><FormattedMessage id="topicsSection.topic" defaultMessage="Topic" /></Typography>
                                                        </Box>
                                                        {mcqChoicesForHeader.map((choiceData, idx) => (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    p: 2,
                                                                    textAlign: "center",
                                                                    fontWeight: "bold",
                                                                    background: "#f0f4f8",
                                                                    color: "#1e3a5f",
                                                                    borderLeft: "1px solid #cbd5e1",
                                                                    borderBottom: "2px solid #cbd5e1",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    minWidth: mcqChoicesForHeader.length < 7 ? 200 : 100,
                                                                    maxWidth: mcqChoicesForHeader.length < 7 ? 300 : 100,
                                                                }}
                                                            >
                                                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}>{choiceData.name}</Typography>
                                                                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#64748b", fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif' }}>
                                                                    {isSignals
                                                                        ? intl.formatMessage({ id: "topicsSection.responded_flags", defaultMessage: "{y} responded · {x} flags" }, { x: choiceData.clustered, y: choiceData.groupTotal })
                                                                        : intl.formatMessage({ id: "topicsSection.responded_fraction", defaultMessage: "{x}/{y} Responded" }, { x: choiceData.clustered, y: choiceData.groupTotal })}
                                                                </Typography>
                                                            </Box>
                                                        ))}

                                                        {/* Table Rows - each topic is one row of grid cells */}
                                                        {topics.map((topic, topicIndex) => (
                                                            <React.Fragment key={topicIndex}>
                                                                {/* Topic Name Column */}
                                                                <Box sx={{
                                                                    p: 2,
                                                                    borderRight: "1px solid #e0e0e0",
                                                                    borderBottom: "1px solid #e0e0e0",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    justifyContent: "space-between",
                                                                    bgcolor: "transparent",
                                                                    "&:hover": { bgcolor: "rgba(25, 118, 210, 0.04)" }
                                                                }}>
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                                                                        {isSignals && (
                                                                            <Tooltip title={topic.is_negative
                                                                                ? intl.formatMessage({ id: "topicsSection.signal_risk", defaultMessage: "Risk (negative signal)" })
                                                                                : intl.formatMessage({ id: "topicsSection.signal_positive", defaultMessage: "Positive signal" })}>
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        width: 10,
                                                                                        height: 10,
                                                                                        borderRadius: "50%",
                                                                                        flexShrink: 0,
                                                                                        bgcolor: topic.is_negative ? "#c62828" : "#2e7d32",
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        )}
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                fontWeight: isSignals ? 600 : 400,
                                                                                fontSize: "0.95rem",
                                                                                wordWrap: "break-word",
                                                                                lineHeight: 1.4,
                                                                                color: isSignals ? (topic.is_negative ? "#c62828" : "#2e7d32") : "#333",
                                                                                textTransform: isSignals ? "capitalize" : "none",
                                                                                fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                                                            }}
                                                                        >
                                                                            {resolveTitle(questionText, topicIndex, topic.title)}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: "0.85rem",
                                                                                color: "#1a1a1a",
                                                                                fontWeight: 700,
                                                                                fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                                                            }}
                                                                        >
                                                                            {intl.formatMessage({ id: "topicsSection.responses_count", defaultMessage: "{count} responses" }, { count: topic.count })}
                                                                        </Typography>
                                                                        <Tooltip title={intl.formatMessage({ id: "topicsSection.view_details", defaultMessage: "View Details" })}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleViewTopicDetails(topic, questionText, topicIndex)}
                                                                                sx={{
                                                                                    p: 0.5,
                                                                                    color: "primary.main",
                                                                                    "&:hover": {
                                                                                        bgcolor: "rgba(25, 118, 210, 0.1)"
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <InfoIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </Box>

                                                                {/* MCQ Choice Columns */}
                                                                {mcqChoicesWithCounts.map((choiceData, choiceIdx) => {
                                                                    const choice = choiceData.name;
                                                                    // Signals variant: use the JSON's own per-cell figures
                                                                    // (count, diff, pct_in_signal, pct_in_all, totals) instead
                                                                    // of the topic heuristics below. Topics path is unchanged.
                                                                    const signalCell = isSignals ? topic.attr_cells?.[choice] : null;

                                                                    const count = signalCell ? (signalCell.count || 0) : (topic.attr_topic_count?.[choice] || 0);
                                                                    const difference = (signalCell ? (signalCell.diff || 0) : (topic.attr_diff?.[choice] || 0)) * 100;

                                                                    // TOPIC STATS: count out of totalCount (topicPercentage).
                                                                    const totalCount = signalCell ? (signalCell.total_sig_answered || 0) : topic.count;
                                                                    const topicPercentage = signalCell
                                                                        ? (signalCell.pct_in_signal || 0) * 100
                                                                        : (totalCount > 0 ? ((count / totalCount) * 100) : 0);

                                                                    // FILTER STATS: totalChoiceCount out of avgResponsesPerTopic (overallPercentage).
                                                                    let totalChoiceCount;
                                                                    let avgResponsesPerTopic;
                                                                    let overallPercentage;
                                                                    if (signalCell) {
                                                                        avgResponsesPerTopic = signalCell.total_all_answered || 0;
                                                                        totalChoiceCount = Math.round((signalCell.pct_in_all || 0) * avgResponsesPerTopic);
                                                                        overallPercentage = (signalCell.pct_in_all || 0) * 100;
                                                                    } else {
                                                                        totalChoiceCount = Math.floor(Number(choiceData.selfCount) || 0);
                                                                        const flattenedTopics = Object.values(topicsData).flat();
                                                                        const topicCount = Object.keys(topicsData).length;
                                                                        const totalResponses = Math.round(flattenedTopics.reduce((sum, t) => sum + (Number(t.count) || 0), 0));
                                                                        avgResponsesPerTopic = topicCount > 0 ? Math.round(totalResponses / topicCount) : 0;
                                                                        overallPercentage = avgResponsesPerTopic > 0 ? ((totalChoiceCount / avgResponsesPerTopic) * 100) : 0;
                                                                    }

                                                                    // Responsive font sizes based on number of columns
                                                                    const columnCount = mcqChoicesWithCounts.length;
                                                                    const percentageFontSize = columnCount <= 2 ? '1.25rem' : columnCount <= 4 ? '1.1rem' : columnCount <= 6 ? '0.95rem' : '0.85rem';
                                                                    const descFontSize = columnCount <= 2 ? '0.8rem' : columnCount <= 4 ? '0.75rem' : columnCount <= 6 ? '0.7rem' : '0.65rem';

                                                                    return (
                                                                        <Tooltip
                                                                            key={choiceIdx}
                                                                            title={
                                                                                <Box sx={{ minWidth: 220, border: '1px solid #e0e0e0', borderRadius: '4px', pt: 1.5 }}>
                                                                                    {/* Header */}

                                                                                    {/* Topic Section */}
                                                                                    <Box sx={{ px: 1.5, mb: 1.5 }}>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                                            <Box sx={{
                                                                                                width: 8,
                                                                                                height: 8,
                                                                                                borderRadius: '50%',
                                                                                                bgcolor: '#1976d2',
                                                                                                mr: 1
                                                                                            }} />
                                                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#90caf9' }}>
                                                                                                <FormattedMessage id="topicsSection.topic_stats" defaultMessage="TOPIC STATS" />
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box sx={{ pl: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 0.5 }}>
                                                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                                                                                {intl.formatMessage({ id: "topicsSection.responses_ratio", defaultMessage: "{count} / {total} responses" }, { count, total: totalCount })}
                                                                                            </Typography>
                                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                                ({topicPercentage.toFixed(2)}%)
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Box>

                                                                                    {/* Divider */}
                                                                                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', mx: 1.5, mb: 1.5 }} />

                                                                                    {/* Overall Section */}
                                                                                    <Box sx={{ px: 1.5, mb: 1.5 }}>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                                            <Box sx={{
                                                                                                width: 8,
                                                                                                height: 8,
                                                                                                borderRadius: '50%',
                                                                                                bgcolor: '#9c27b0',
                                                                                                mr: 1
                                                                                            }} />
                                                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ce93d8' }}>
                                                                                                <FormattedMessage id="topicsSection.filter_stats" defaultMessage="FILTER STATS" />
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box sx={{ pl: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 0.5 }}>
                                                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                                                                                {intl.formatMessage({ id: "topicsSection.responses_ratio", defaultMessage: "{count} / {total} responses" }, { count: totalChoiceCount, total: avgResponsesPerTopic })}
                                                                                            </Typography>
                                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                                ({overallPercentage.toFixed(2)}%)
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Box>

                                                                                    {/* Difference Footer */}
                                                                                    <Box sx={{
                                                                                        bgcolor: difference > 0 ? 'rgba(46, 125, 50, 0.5)' : difference < 0 ? 'rgba(198, 40, 40, 0.5)' : 'rgba(255,255,255,0.1)',
                                                                                        px: 1.5,
                                                                                        py: 1,
                                                                                        borderRadius: '0 0 4px 4px',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center'
                                                                                    }}>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                            <FormattedMessage id="topicsSection.difference" defaultMessage="Difference:" />
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                fontWeight: 700,
                                                                                                color: difference > 0 ? '#81c784' : difference < 0 ? '#ef9a9a' : 'white'
                                                                                            }}
                                                                                        >
                                                                                            {difference > 0 ? "+" : ""}{difference.toFixed(2)}%
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            }
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    p: { xs: 1, sm: 1.5, md: 2 },
                                                                                    textAlign: "center",
                                                                                    bgcolor: getCellBackgroundColor(difference),
                                                                                    borderLeft: "1px solid #e0e0e0",
                                                                                    borderBottom: "1px solid #e0e0e0",
                                                                                    cursor: "pointer",
                                                                                    transition: "all 0.2s ease",
                                                                                    "&:hover": {
                                                                                        transform: "scale(1.02)",
                                                                                        boxShadow: "inset 0 0 8px rgba(0,0,0,0.2)",
                                                                                        zIndex: 1
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, mb: 0.5 }}>
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontWeight: 700,
                                                                                            fontSize: percentageFontSize,
                                                                                            color: difference > 0 ? "#2e7d32" : difference < 0 ? "#c62828" : "text.primary",
                                                                                            lineHeight: 1.2
                                                                                        }}
                                                                                    >
                                                                                        {difference > 0 && "+"}{difference.toFixed(2)}%
                                                                                    </Typography>
                                                                                    {reportId && (
                                                                                        <Tooltip title={intl.formatMessage({ id: "topicsSection.ask_why", defaultMessage: "Ask why" })}>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleCellInsight({
                                                                                                        questionTitle: questionText,
                                                                                                        pillar,
                                                                                                        topicName: topic.title,
                                                                                                        groupName: choice,
                                                                                                        colorLabel: difference > 0 ? 'green' : difference < 0 ? 'red' : 'neutral',
                                                                                                        cellValue: `${difference > 0 ? "+" : ""}${difference.toFixed(2)}%`,
                                                                                                        topicResponses: totalCount,
                                                                                                        groupCountInTopic: count,
                                                                                                        topicPercentage: topicPercentage.toFixed(2),
                                                                                                        totalGroupCount: totalChoiceCount,
                                                                                                        avgResponsesPerTopic,
                                                                                                        overallPercentage: overallPercentage.toFixed(2),
                                                                                                    });
                                                                                                }}
                                                                                                sx={{
                                                                                                    p: 0.25,
                                                                                                    color: difference > 0 ? "#2e7d32" : difference < 0 ? "#c62828" : "text.disabled",
                                                                                                    '&:hover': {
                                                                                                        bgcolor: difference > 0 ? 'rgba(46, 125, 50, 0.12)' : difference < 0 ? 'rgba(198, 40, 40, 0.12)' : 'rgba(0,0,0,0.04)',
                                                                                                    },
                                                                                                }}
                                                                                            >
                                                                                                <AutoAwesomeIcon sx={{ fontSize: '0.75rem' }} />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </Box>
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        gap: 0.5
                                                                                    }}
                                                                                >
                                                                                    <People sx={{ fontSize: descFontSize, color: 'text.secondary' }} />
                                                                                    <Typography
                                                                                        color="text.secondary"
                                                                                        sx={{
                                                                                            fontSize: descFontSize,
                                                                                            lineHeight: 1.3
                                                                                        }}
                                                                                    >
                                                                                        {count}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        </Tooltip>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        ))}
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {/* Empty state. The cards block above used to render nothing at all when
                its gate failed, which hid perfectly good topic data. Anything that
                leaves no card to draw now says so out loud. */}
            {pillarTopicEntries.length === 0 && (effectivePillar || !hasAnyTopicPillar) && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                            {effectivePillar
                                ? intl.formatMessage(
                                    { id: "topicsSection.no_topics_for_pillar", defaultMessage: "No topics found for {pillar}." },
                                    { pillar: effectivePillar }
                                )
                                : intl.formatMessage({ id: "topicsSection.no_topics_to_display", defaultMessage: "No topics to display." })}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Topic Detail Modal */}
            <TopicDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                topic={selectedTopic}
                questionTitle=""
                statsData={statsData}
                reportId={reportId}
                question={selectedTopicMeta.question}
                topicIndex={selectedTopicMeta.topicIndex}
                initialTitle={resolveTitle(selectedTopicMeta.question, selectedTopicMeta.topicIndex, selectedTopic?.title)}
                onTitleSaved={handleTitleSaved}
            />

            {/* Recompute Topics Dialog */}
            <TopicRecomputeDialog
                open={recomputeQuestion != null}
                onClose={() => setRecomputeQuestion(null)}
                reportId={reportId}
                question={recomputeQuestion}
                currentTopics={
                    recomputeQuestion != null
                        ? (topicOverrides[recomputeQuestion] || topicsData[recomputeQuestion] || [])
                        : []
                }
                onApplied={(topics) => handleTopicsRecomputed(recomputeQuestion, topics)}
            />

            {/* Cell Insight Dialog */}
            <CellInsightDialog
                open={cellInsightOpen}
                onClose={() => setCellInsightOpen(false)}
                reportId={reportId}
                query={cellInsightQuery}
            />
        </Box>
    );
};

export default TopicsSection;